/**
 * ExaminerGrantsView (P358) -- mint / list / revoke examiner grants +
 * export the offline examiner pack. Route: /admin/examiner-grants.
 *
 * The bank-side half of the Examiner Workspace ceremony (P357): each
 * mint/revoke is audited AND sealed into the examiner-grant chain.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api, currentAuthToken } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { useToast } from "shared/toast/ToastProvider";

interface ExaminerGrant {
  id: string;
  examiner_username: string;
  scope_note: string;
  expires_at: string;
  revoked_at: string | null;
  is_active: boolean;
  created_at: string;
  /** Round 13 G10 -- who minted it, so two live grants for one
   *  examiner are two records rather than one printed twice. */
  created_by_username?: string | null;
  /** Round 16 H30 -- the person's name, as every other surface gives it. */
  created_by_name?: string | null;
  /** Round 18 S1 -- has anyone signed in with this username? */
  account_known?: boolean;
  /** Round 19 S5 -- who revoked it; null while not revoked. */
  revoked_by_name?: string | null;
}

/** Round 18 S1 -- what the identity provider issues as a username. */
export function isUsernameShaped(value: string): boolean {
  return /^[A-Za-z0-9._@-]+$/.test(value.trim());
}

async function downloadExaminerPack(): Promise<void> {
  const token = currentAuthToken();
  const response = await fetch("/v1/admin/exports/examiner-pack.zip", {
    headers: {
      Accept: "application/zip",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "marsad-examiner-pack.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export function ExaminerGrantsView() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const grants = useQuery({
    queryKey: ["admin", "examiner-grants"],
    queryFn: () => api.get<ExaminerGrant[]>("/v1/admin/examiner-grants"),
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["admin", "examiner-grants"] });

  const [username, setUsername] = useState("");
  const [scope, setScope] = useState("");
  const [days, setDays] = useState("14");
  // Round 18 S1 -- the form took "CX walk r18 M5" and minted a grant to
  // nobody. A username has no spaces; say so before the mint.
  const usernameOk = !username || isUsernameShaped(username);

  const mint = useMutation({
    mutationFn: () =>
      api.post<ExaminerGrant>("/v1/admin/examiner-grants", {
        examiner_username: username,
        scope_note: scope,
        days: Number(days),
      }),
    onSuccess: (g) => {
      setUsername("");
      setScope("");
      void invalidate();
      toast.push({
        tone: "success",
        message: t("adminExaminer.minted", {
          username: g.examiner_username,
          date: g.expires_at.slice(0, 10),
        }),
      });
    },
    onError: () =>
      toast.push({ tone: "error", message: t("adminExaminer.mintFailed") }),
  });
  const revoke = useMutation({
    mutationFn: (id: string) =>
      api.post<ExaminerGrant>(`/v1/admin/examiner-grants/${id}/revoke`, {}),
    onSuccess: () => {
      void invalidate();
      toast.push({ tone: "warning", message: t("adminExaminer.revoked") });
    },
  });
  const pack = useMutation({
    mutationFn: downloadExaminerPack,
    onSuccess: () =>
      toast.push({ tone: "success", message: t("adminExaminer.packDone") }),
    onError: () =>
      toast.push({ tone: "error", message: t("adminExaminer.packFailed") }),
  });

  return (
    <article data-testid="examiner-grants-view" className="max-w-4xl space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="dual-signoff"
            className="me-2 inline-block h-6 w-6 align-[-4px]"
          />
          {t("adminExaminer.heading")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">{t("adminExaminer.body")}</p>
      </header>

      <section className="space-y-2 rounded-xl border border-paper-2 bg-white p-4 shadow-card">
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-600">
            {t("adminExaminer.username")}
            <input
              dir="auto"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              data-testid="grant-username"
              className="mt-1 block w-48 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs text-slate-600">
            {t("adminExaminer.scope")}
            <input
              dir="auto"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              data-testid="grant-scope"
              className="mt-1 block w-72 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="text-xs text-slate-600">
            {t("adminExaminer.days")}
            <input
              dir="auto"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              data-testid="grant-days"
              className="mt-1 block w-16 rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={() => mint.mutate()}
            disabled={!username || !usernameOk || !scope || mint.isPending}
            data-testid="grant-mint"
            className="rounded bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {t("adminExaminer.mint")}
          </button>
        </div>
        {!usernameOk && (
          <p data-testid="grant-username-invalid" className="text-xs text-amber-800">
            {t("adminExaminer.usernameInvalid")}
          </p>
        )}
        <p className="text-xs text-slate-500">{t("adminExaminer.mintNote")}</p>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          {t("adminExaminer.grants")}
        </h2>
        {(grants.data ?? []).length === 0 ? (
          <p data-testid="grants-empty" className="text-sm text-slate-400">
            {t("adminExaminer.empty")}
          </p>
        ) : (
          <ul className="space-y-1">
            {(grants.data ?? []).map((g) => (
              /* ROUND 13 G10 -- the row is keyed on the GRANT, not on
                 the examiner. Two live grants for one examiner shared
                 one control identity, so the page held two Revoke
                 buttons a test (or a screen reader, or a script)
                 could not tell apart, and revoking one announced that
                 access had closed while the other kept it open. */
              <li
                key={g.id}
                data-testid={`grant-row-${g.id}`}
                data-examiner={g.examiner_username}
                className="flex flex-wrap items-baseline gap-x-3 rounded border border-paper-2 bg-white px-3 py-2 text-sm"
              >
                {/* Round 18 S1 -- the row labels whose grant it is: it
                    read "CX walk r18 M5 · r18 grant" with nothing
                    saying which was the person. */}
                <span className="font-medium text-slate-900">
                  {t("adminExaminer.rowExaminer", { username: g.examiner_username })}
                </span>
                <span className="text-xs text-slate-600">
                  {t("adminExaminer.rowScope", { scope: g.scope_note })}
                </span>
                {/* What actually separates two grants for one person:
                    when it was minted and by whom. The examiner-grant
                    chain three panels below has sealed both since
                    P357; the register simply never read them back. */}
                <span
                  data-testid={`grant-minted-${g.id}`}
                  className="text-xs text-slate-500"
                >
                  {t("adminExaminer.mintedBy", {
                    when: g.created_at.slice(0, 16).replace("T", " "),
                    who:
                      g.created_by_name ??
                      g.created_by_username ??
                      t("adminExaminer.mintedByUnrecorded"),
                  })}
                </span>
                {g.is_active && g.account_known === false ? (
                  <span
                    data-testid={`grant-unbound-${g.id}`}
                    className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-900"
                  >
                    {t("adminExaminer.unbound", {
                      username: g.examiner_username,
                      date: g.expires_at.slice(0, 10),
                    })}
                  </span>
                ) : g.is_active ? (
                  <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-800">
                    {t("adminExaminer.activeUntil", {
                      date: g.expires_at.slice(0, 10),
                    })}
                  </span>
                ) : (
                  <span
                    data-testid={`grant-ended-${g.id}`}
                    className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600"
                  >
                    {/* Round 19 S5 -- who revoked it, and when. */}
                    {g.revoked_at
                      ? t("adminExaminer.revokedAtBy", {
                          when: g.revoked_at.slice(0, 16).replace("T", " "),
                          who:
                            g.revoked_by_name ?? t("adminExaminer.mintedByUnrecorded"),
                        })
                      : t("adminExaminer.expiredPill")}
                  </span>
                )}
                {g.is_active && (
                  <button
                    type="button"
                    onClick={() => revoke.mutate(g.id)}
                    data-testid={`grant-revoke-${g.id}`}
                    className="ms-auto text-xs text-red-600 hover:underline"
                  >
                    {t("adminExaminer.revoke")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2 rounded-xl border border-paper-2 bg-white p-4 shadow-card">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          {t("adminExaminer.offline")}
        </h2>
        <p className="max-w-3xl text-sm text-slate-500">
          {t("adminExaminer.offlineBody")}
        </p>
        <button
          type="button"
          onClick={() => pack.mutate()}
          disabled={pack.isPending}
          data-testid="examiner-pack-download"
          className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-sm font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
        >
          {pack.isPending
            ? t("adminExaminer.packBuilding")
            : t("adminExaminer.packButton")}
        </button>
      </section>
    </article>
  );
}
